import { Component, input, output } from "@angular/core";
import { ButtonModule } from "primeng/button";

export type ButtonSeverity =
  | "success"
  | "info"
  | "warn"
  | "danger"
  | "help"
  | "primary"
  | "secondary"
  | "contrast";

export interface VerticalButton {
  /** Text shown on the button. */
  label: string;
  /** Semantic color. */
  severity?: ButtonSeverity;
  /** Click handler for this button (the "method to implement"). */
  action?: () => void;
  /** Stable identifier emitted alongside `buttonClick`. */
  value?: string | number;
  disabled?: boolean;
  outlined?: boolean;
  loading?: boolean;
  text?: boolean;
  class?: string;
  /** Extra style class(es), e.g. `bqa-danger-btn`. */
  styleClass?: string;
  testId?: string;
}

/**
 * Standardized vertical stack of full-width buttons.
 *
 * The caller specifies the buttons via the `buttons` array where each entry
 * declares:
 *   1. how many buttons (array length),
 *   2. its `severity`, and
 *   3. its `action` (the click method to run).
 *
 * Mirrors the stacked footer used on forms like `bq9-not-complete`.
 */
@Component({
  selector: "app-vertical-button-group",
  standalone: true,
  imports: [ButtonModule],
  templateUrl: "./vertical-button-group.component.html",
  styleUrl: "./vertical-button-group.component.scss"
})
export class VerticalButtonGroupComponent {
  /** Buttons rendered top to bottom; the array length sets the count. */
  buttons = input<VerticalButton[]>([]);
  /** Render each button full width (default). */
  fullWidth = input<boolean>(true);
  testId = input<string>("app-vertical-button-group");

  /** Emitted with the clicked button (in addition to running its `action`). */
  readonly buttonClick = output<VerticalButton>();

  protected resolveStyleClass(button: VerticalButton): string {
    const classes = [button.styleClass ?? ""];
    if (this.fullWidth()) {
      classes.push("p-fullwidth-button");
    }
    return classes.join(" ").trim();
  }

  protected handleClick(button: VerticalButton): void {
    if (button.disabled) {
      return;
    }
    button.action?.();
    this.buttonClick.emit(button);
  }
}
